import {
  QUERY_PRODUCT_ENTITLEMENTS,
  QUERY_PROJECT_ARCHIVED_STATUS,
  QUERY_PROJECTS_PK
} from "services/graphQL/queries";
import { useCallback, useEffect, useMemo } from "react";
import { PROJECT_INTG_CONFIGS } from "services/graphQL/ciq-queries";
import { getGQL, isProjectInIntegrationMode } from "utils/utils";
import { TAuditChangeLog } from "change-events/change-event-polling";
import { useQuery } from "@apollo/client";
import { EProjectStatusId } from "constants/index";
import { useCIQQuery } from "./ciq-gql-hooks";
import { useGetProjectCalendarHolidaysAndWeekends } from "./project-cal-holidays";
import { useSubmittalMaterialLogsHeaders } from "./column-headers";
import { useGetACCImportLogData } from "./project-import-log";

type TAuditChangeLogs = Array<TAuditChangeLog>;

export const useProjectGlobalDataSource = (
  eventLogs: TAuditChangeLogs,
  gqlClientForProject: any,
  additionalParams: any
) => {
  const { projectId } = additionalParams;

  const { data: productEntitlementsData, refetch: refetchProductEntitlements } =
    useQuery<{
      get_product_entitlements: Array<{
        feature: string;
        enabled: boolean;
      }>;
    }>(QUERY_PRODUCT_ENTITLEMENTS, {
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

  const isFeatureFlagEnabled = useCallback(
    (feature: string | undefined) => {
      if (productEntitlementsData?.get_product_entitlements) {
        return productEntitlementsData?.get_product_entitlements.some(
          (f) => f.enabled && f.feature === feature
        );
      }
      return false;
    },
    [productEntitlementsData?.get_product_entitlements]
  );

  const isProductEntitlementsDataAvailable = useMemo(() => {
    return !!productEntitlementsData?.get_product_entitlements;
  }, [productEntitlementsData?.get_product_entitlements]);

  const {
    data: projectDetails,
    loading: projectDetailsLoading,
    error: projectDetailsError,
    refetch: refetchProjectDetails
  } = useCIQQuery(QUERY_PROJECTS_PK, {
    client: gqlClientForProject,
    variables: { projectId },
    skip: !gqlClientForProject
  });

  const {
    data: archivedStatusData,
    loading: archivedStatusLoading,
    refetch: refetchArchivedStatus
  } = useCIQQuery<{
    project_by_pk: { id: string; status_id?: number | null } | null;
  }>(QUERY_PROJECT_ARCHIVED_STATUS, {
    client: gqlClientForProject,
    variables: { projectId },
    skip: !gqlClientForProject || !projectId
  });
  const isProjectArchived =
    archivedStatusData?.project_by_pk?.status_id === EProjectStatusId.Archived;
  const isArchivedStatusResolved =
    !!gqlClientForProject && !archivedStatusLoading;

  const { calendarOffs, refetch: refetchCalendarData } =
    useGetProjectCalendarHolidaysAndWeekends(projectId, gqlClientForProject);

  const { columnHeaders, refetch: refetchHeaders } =
    useSubmittalMaterialLogsHeaders(gqlClientForProject);

  const { query: projectIntgQueryDef } = getGQL(PROJECT_INTG_CONFIGS);

  const { data: projectIntgConfigs } = useCIQQuery(projectIntgQueryDef, {
    client: gqlClientForProject,
    skip: !isProjectInIntegrationMode(projectDetails?.project_by_pk?.mode)
  });

  const { lastImportStatus, refetchIntegrationImportLogs } =
    useGetACCImportLogData(gqlClientForProject);

  const handleUpdatesForProjectDetails = useCallback(
    (logs: TAuditChangeLogs) => {
      const projectUpdated = logs.some(
        (event: TAuditChangeLog) =>
          event.project_id === projectId &&
          ((event.data_source === "project" &&
            event.change_type === "update") ||
            event.data_source === "project_product_entitlements")
      );
      if (!projectUpdated) return;

      if (
        logs.some(
          (event: TAuditChangeLog) =>
            event.project_id === projectId &&
            event.data_source === "project" &&
            event.change_type === "update"
        )
      ) {
        refetchProjectDetails();
        refetchArchivedStatus().catch(() => {});
      }
      if (
        logs.some(
          (event: TAuditChangeLog) =>
            event.project_id === projectId &&
            event.data_source === "project_product_entitlements"
        )
      ) {
        refetchProductEntitlements();
      }
    },
    [
      projectId,
      refetchProductEntitlements,
      refetchProjectDetails,
      refetchArchivedStatus
    ]
  );

  const handleUpdatesForCalendar = useCallback(
    (logs: TAuditChangeLogs) => {
      const calendarEventSources = [
        "calendar_shift_mapping",
        "gantt_calendar",
        "calendar_holiday_list"
      ];
      const calendarChangeLog = logs.find((event: TAuditChangeLog) =>
        calendarEventSources.includes(event.data_source)
      );
      if (calendarChangeLog && calendarChangeLog.project_id === projectId) {
        refetchCalendarData();
      }
    },
    [projectId, refetchCalendarData]
  );

  const handleUpdatesForHeaders = useCallback(
    (logs: TAuditChangeLogs) => {
      const headersChangeLog = logs.find(
        (event: TAuditChangeLog) =>
          event.data_source === "project_feature_configurations"
      );
      if (headersChangeLog && headersChangeLog.project_id === projectId) {
        refetchHeaders();
      }
    },
    [projectId, refetchHeaders]
  );

  const handleACCImportLog = useCallback(
    (logs: TAuditChangeLogs) => {
      const accImportLog = logs.find(
        (event: TAuditChangeLog) => event.data_source === "import_log"
      );
      if (accImportLog && accImportLog.project_id === projectId) {
        refetchIntegrationImportLogs();
      }
    },
    [projectId, refetchIntegrationImportLogs]
  );

  useEffect(() => {
    if (!eventLogs || !eventLogs.length) return;
    handleUpdatesForProjectDetails(eventLogs);
    handleUpdatesForCalendar(eventLogs);
    handleUpdatesForHeaders(eventLogs);
    handleACCImportLog(eventLogs);
  }, [
    eventLogs,
    handleUpdatesForCalendar,
    handleUpdatesForHeaders,
    handleUpdatesForProjectDetails,
    handleACCImportLog
  ]);

  return {
    projectDetails,
    projectDetailsLoading,
    projectDetailsError,
    calendarOffs,
    columnHeaders,
    projectIntgConfigs,
    lastImportStatus,
    isFeatureFlagEnabled,
    isProductEntitlementsDataAvailable,
    isProjectArchived,
    isArchivedStatusResolved
  };
};
